<template>
    <div>
        <div v-if="!innerIsReview">
            <v-fade-transition mode="out-in">
                <div v-show="innerShouldShow">
                    <span
                        class="field-directions"
                        v-if="directions"
                        v-html="directions"
                    ></span>
                    <div class="d-flex">
                        <validation-provider
                            v-slot="{ errors }"
                            v-bind="providerProps"
                            ref="vp"
                            :name="label || 'This'"
                            :rules="rules"
                            mode="eager"
                            tag="div"
                            class="flex-grow-1 phone__container"
                            :vid="'validate-' + fieldAssignmentId"
                        >
                            <v-text-field
                                v-bind="vProps"
                                ref="tel-input"
                                v-model="numberModel"
                                :error-messages="errors"
                                :prepend-icon="computedIcon"
                                :type="enableValidation ? 'tel' : 'text'"
                                @keydown="manuallyTyping = true"
                                class="phone__number"
                                hide-details="auto"
                            >
                                <template #label>
                                    <span v-if="innerLabel" v-html="innerLabel"></span>
                                </template>
                            </v-text-field>
                            <v-autocomplete
                                v-model="countryModel"
                                :items="countryData"
                                item-value="iso2"
                                item-text="name"
                                :filter="countryFilter"
                                hide-selected
                                return-object
                                class="phone__country"
                                @change="changeCountry"
                            >
                                <template #selection="{ item }">
                                    <div
                                        :class="item.iso2.toLowerCase()"
                                        class="vti__flag"
                                    />
                                </template>
                                <template #item="{ item }">
                                    <span
                                        :class="item.iso2.toLowerCase()"
                                        class="vti__flag"
                                    />
                                    <span
                                        >{{ item.name }}
                                        {{ `+${item.dialCode}` }}</span
                                    >
                                </template>
                            </v-autocomplete>
                        </validation-provider>
                    </div>
                </div>
            </v-fade-transition>
        </div>
        <div v-else>
            <type-review
                v-if="extraProps"
                :label="label"
                :directions="directions"
                :should-show="innerShouldShow"
                :show-edit-button="extraProps.editButton"
                :page-number="extraProps.originalPageNumber"
                @goToPage="goToPage($event)"
            >
                <div v-if="innerValue">
                    <span v-html="innerValue"></span>
                </div>
                <div v-else>N/A</div>
            </type-review>
        </div>
    </div>
</template>

<script>
import AwesomePhone from 'awesome-phonenumber';
import formMixin from '../formMixin';
import countryData from '../countryData';
import TypeReview from './TypeReview';

const initialCountry = { ...countryData[0] };
export default {
    name: 'TypePhone',
    mixins: [formMixin],
    components: {
        TypeReview
    },
    props: {
        showIcon: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            countryData,
            countryModel: initialCountry,
            countryChanged: false, // track if changed to override provided validation
            manuallyTyping: false
        };
    },
    mounted() {
        this.$nextTick(() => {
            if( this.field_path ) {
                const value = this.getSiblingValue(this.field_path.replace('.phone', '.country'));
                if( value ) {
                    let f = countryData.find(x => x.iso2 == value);
                    if( f ) this.countryModel = f;
                }
            }
        });

        this.fieldLoaded(); // signal to the form we are loaded

        // see if country changes
        this.$root.$on('fieldChanged', data => {
            let {field, value} = data;
            if( field.field_path?.includes('.country') ) {
                if( !value )
                    value = this.getSiblingValue(field.field_path);

                if( value ) {
                    let f = countryData.find(x => x.iso2 == value);
                    if( f ) this.countryModel = f;
                }
            }
        });
    },
    computed: {
        defaults() {
            return {
                label: this.innerLabel,
                directions: this.directions,
                placeholder: AwesomePhone.getExample(
                    this.countryCode
                ).getNumber('national'),
                persistentHint: true,
                persistentPlaceholder: true
            };
        },
        rules() {
            return (this.enableValidation) ? {
                ...this.validation_settings,
                required:
                    !!this.validation_settings.required &&
                    !!this.innerShouldShow,
                phone: {
                    country: this.countryCode
                }
            } : {};
        },
        computedIcon() {
            return this.showIcon ? 'mdi-phone' : undefined;
        },
        countryCode() {
            if (
                !this.countryChanged &&
                typeof this.validation === 'object' &&
                Object.prototype.hasOwnProperty.call(
                    this.validation,
                    'countryCode'
                ) &&
                typeof this.validation.countryCode === 'string' &&
                this.validation.countryCode !== ''
            ) {
                return this.validation.countryCode;
            }
            return this.countryModel.iso2;
        },
        numberModel: {
            get() {
                return this.innerValue === ''
                    ? new AwesomePhone(
                          this.innerValue,
                          this.countryCode
                      ).getNumber('national')
                    : AwesomePhone.getAsYouType(this.countryCode).reset(
                          this.innerValue
                      );
            },
            set(v) {
                const unformatted = v.replace(/\D/g, ''); // remove non digit chars
                this.innerValue = unformatted;
            }
        }
    },
    watch: {
        value(v) {
            if (!this.manuallyTyping) this.doValidation(); // validate external changes (like autofill from gmaps)

            this.manuallyTyping = false;
        }
    },
    methods: {
        doValidation() {
            // force re-validation
            this.$refs.vp.validate(this.innerValue).then(() => {
                this.$refs['tel-input'].$refs.input.focus();
                this.$refs['tel-input'].$refs.input.blur();
            });
        },
        changeCountry() {
            this.countryChanged = true;
            this.doValidation();
        },
        countryFilter(item, queryText, itemText) {
            const countryName = item.name.toLowerCase();
            const countryAbbr = item.iso2.toLowerCase();
            const searchText = queryText.toLowerCase();

            return (
                countryName.includes(searchText) ||
                countryAbbr.includes(searchText)
            );
        }
    }
};
</script>

<style lang="scss" scoped>
// flag styles in ~/assets/css/styles/_phoneInput.scss

.phone {
    &__container {
        position: relative;
    }

    &__country {
        // display: none;
        position: absolute !important;
        top: 0;
        left: 5%;
        max-width: 50px;
        z-index: 1;
    }
}
</style>

<style lang="scss">
@import 'resources/sass/phoneFlag.scss';
// overrides
.phone {
    &__number {
        input {
            padding-left: 20% !important;
        }
    }

    /* &__number.v-input--is-label-active ~ .phone__country,
    &__number:focus-within ~ .phone__country,
    .phone__country:focus-within {
        display: block;
    } */
}
</style>
